// Đọc/ghi lịch ngày lễ, sổ phép năm, đơn nghỉ phép (giai đoạn 6). Luật nghiệp vụ theo lich-ngay-le/action.ts và
// phep-nam/action.ts của V2; số phép đã dùng tính từ đơn đã duyệt (xem core/leaveHoliday.js). Đổi ngày lễ hay
// đơn phép làm chữ ký kỳ lương đổi theo, nên kỳ chưa duyệt tự báo "cần chốt lại" mà không phải ghi gì thêm.
import {
    LEAVE_TYPES, buildLeaveDays, cloneHolidaysToYear, findHolidayOverlap, findLeaveOverlap, holidayDateKeys,
    solarHolidaysOfYear, summarizeLeaveBalance, validateHolidayInput, validateLeaveRequest,
} from '../core/leaveHoliday';
import { buildTeamsByEmployee } from '../core/periodInputs';
import { newId } from './appsheet';
import { writeAudit } from './configActions';
import { assertCanManagePayroll, currentPayrollUser } from './permissions';
import { payrollRepos } from './tables';

const formatDay = (date) => date.toLocaleDateString('vi-VN');

// ===== LỊCH NGÀY LỄ =====

export async function loadHolidayData() {
    const [holidays, employees, allocations] = await Promise.all([
        payrollRepos.holidays.list(),
        payrollRepos.employees.list(),
        payrollRepos.allocations.list(),
    ]);
    const teams = [...new Set(allocations.map((allocation) => allocation.TO).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'vi', { numeric: true }));
    return { holidays: holidays.filter((holiday) => holiday.HIEU_LUC !== false), employees, teams };
}

function holidayRow(holiday, { isNew, confirm }) {
    const user = currentPayrollUser().username;
    const scope = holiday.PHAM_VI || 'TOAN_CONG_TY';
    return {
        ID: holiday.ID || newId('NL'),
        MA_NGAY_LE: String(holiday.MA_NGAY_LE || '').trim().toUpperCase() || `NL_${holiday.TU_NGAY.getFullYear()}_${holiday.TU_NGAY.getMonth() + 1}_${holiday.TU_NGAY.getDate()}`,
        TEN_NGAY_LE: String(holiday.TEN_NGAY_LE).trim(),
        TU_NGAY: holiday.TU_NGAY,
        DEN_NGAY: holiday.DEN_NGAY || holiday.TU_NGAY,
        SO_NGAY_HUONG_LUONG: Math.max(0, Number(holiday.SO_NGAY_HUONG_LUONG) || 0),
        LOAI_NGAY: holiday.LOAI_NGAY || 'HUONG_LUONG',
        PHAM_VI: scope,
        TO: scope === 'TO' ? holiday.TO : '',
        NHAN_SU_ID: scope === 'NHAN_SU' ? holiday.NHAN_SU_ID : '',
        HIEU_LUC: true,
        DA_XAC_NHAN: Boolean(confirm),
        NGUOI_XAC_NHAN: confirm ? (holiday.DA_XAC_NHAN && holiday.NGUOI_XAC_NHAN ? holiday.NGUOI_XAC_NHAN : user) : '',
        NGAY_XAC_NHAN: confirm ? (holiday.DA_XAC_NHAN && holiday.NGAY_XAC_NHAN ? holiday.NGAY_XAC_NHAN : new Date()) : null,
        MO_TA: holiday.MO_TA || '',
        NGUON: holiday.NGUON || 'NHAP_TAY',
        ...(isNew ? { NGUOI_TAO: user } : {}),
    };
}

/** Thêm hoặc sửa ngày lễ. Sửa ngày lễ đã xác nhận thì vẫn giữ xác nhận nếu người sửa chọn giữ. */
export async function saveHoliday(holiday, { isNew, confirm, existing }) {
    assertCanManagePayroll();
    const errors = validateHolidayInput(holiday);
    if (errors.length) throw new Error(errors.join(' '));
    const row = holidayRow(holiday, { isNew, confirm });
    const overlap = findHolidayOverlap(existing, row);
    if (overlap) throw new Error(`Chồng ngày với ${overlap.MA_NGAY_LE} - ${overlap.TEN_NGAY_LE} (${formatDay(overlap.TU_NGAY)} – ${formatDay(overlap.DEN_NGAY)}).`);
    if (isNew) await payrollRepos.holidays.add([row]);
    else await payrollRepos.holidays.update([row]);
    await writeAudit(isNew ? 'Thêm ngày lễ' : 'Sửa ngày lễ', `${row.MA_NGAY_LE} ${row.TEN_NGAY_LE} ${formatDay(row.TU_NGAY)} – ${formatDay(row.DEN_NGAY)}`);
    return row;
}

export async function confirmHolidays(holidays) {
    assertCanManagePayroll();
    const user = currentPayrollUser().username;
    const rows = holidays.filter((holiday) => !holiday.DA_XAC_NHAN).map((holiday) => ({ ID: holiday.ID, DA_XAC_NHAN: true, NGUOI_XAC_NHAN: user, NGAY_XAC_NHAN: new Date() }));
    if (!rows.length) return 0;
    await payrollRepos.holidays.update(rows);
    await writeAudit('Xác nhận ngày lễ', holidays.map((holiday) => holiday.MA_NGAY_LE).join(', '));
    return rows.length;
}

export async function deleteHoliday(holiday) {
    assertCanManagePayroll();
    await payrollRepos.holidays.remove([holiday.ID]);
    await writeAudit('Xóa ngày lễ', `${holiday.MA_NGAY_LE} ${holiday.TEN_NGAY_LE}`);
}

/** Thêm các dòng mới (nhân bản năm, ngày lễ dương lịch, nhập Excel) ở trạng thái chờ xác nhận, bỏ dòng chồng ngày. */
async function addHolidayRows(candidates, existing, action) {
    const rows = [];
    let skipped = 0;
    for (const candidate of candidates) {
        const errors = validateHolidayInput(candidate);
        if (errors.length) throw new Error(`${candidate.TEN_NGAY_LE || candidate.MA_NGAY_LE}: ${errors.join(' ')}`);
        const row = holidayRow({ ...candidate, ID: null }, { isNew: true, confirm: false });
        if (findHolidayOverlap([...existing, ...rows], row)) skipped += 1;
        else rows.push(row);
    }
    if (rows.length) await payrollRepos.holidays.add(rows);
    await writeAudit(action, { them: rows.length, boQuaTrung: skipped });
    return { created: rows.length, skipped };
}

export async function cloneHolidayYear(existing, sourceYear, targetYear) {
    assertCanManagePayroll();
    if (sourceYear === targetYear) throw new Error('Năm nguồn và năm đích phải khác nhau.');
    const { rows, skipped } = cloneHolidaysToYear(existing, sourceYear, targetYear);
    if (!rows.length && !skipped) throw new Error(`Không có lịch ngày lễ năm ${sourceYear}.`);
    const result = await addHolidayRows(rows, existing, `Nhân bản lịch ngày lễ ${sourceYear} → ${targetYear}`);
    return { ...result, skipped: result.skipped + skipped };
}

export async function addSolarHolidays(existing, year) {
    assertCanManagePayroll();
    return addHolidayRows(solarHolidaysOfYear(year), existing, `Thêm ngày lễ dương lịch ${year}`);
}

export async function importHolidays(existing, candidates) {
    assertCanManagePayroll();
    return addHolidayRows(candidates.map((candidate) => ({ ...candidate, NGUON: 'IMPORT_EXCEL' })), existing, 'Nhập lịch ngày lễ từ Excel');
}

// ===== PHÉP NĂM VÀ ĐƠN NGHỈ =====

const balanceId = (soHd, year) => `${soHd}-${year}`;

export async function loadLeaveData() {
    const [balances, requests, holidays, employees, allocations, periods] = await Promise.all([
        payrollRepos.leaveBalances.list(),
        payrollRepos.leaveRequests.list(),
        payrollRepos.holidays.list(),
        payrollRepos.employees.list(),
        payrollRepos.allocations.list(),
        payrollRepos.periods.list(),
    ]);
    return {
        balances: balances.map((balance) => summarizeLeaveBalance(balance, requests)),
        requests,
        holidays: holidays.filter((holiday) => holiday.HIEU_LUC !== false),
        employees,
        teamsByEmployee: buildTeamsByEmployee(employees.filter((employee) => employee.SO_HD), allocations, new Date()),
        approvedPeriods: periods.filter((period) => period.TRANG_THAI === 'DA_DUYET'),
    };
}

/** Khóa sổ theo Số HĐ + năm nên ghi lại cùng một người cùng năm là sửa chứ không thêm dòng trùng. */
export async function saveLeaveBalance({ NHAN_SU_ID, NAM, PHEP_CHUAN, PHEP_TON_NAM_TRUOC, PHEP_DIEU_CHINH, GHI_CHU }, { isNew }) {
    assertCanManagePayroll();
    const row = { ID: balanceId(NHAN_SU_ID, NAM), NHAN_SU_ID, NAM, PHEP_CHUAN: Number(PHEP_CHUAN) || 0, PHEP_TON_NAM_TRUOC: Number(PHEP_TON_NAM_TRUOC) || 0, PHEP_DIEU_CHINH: Number(PHEP_DIEU_CHINH) || 0, GHI_CHU: GHI_CHU || '', UPDATED_AT: new Date() };
    if (isNew) await payrollRepos.leaveBalances.add([row]);
    else await payrollRepos.leaveBalances.update([row]);
    await writeAudit(isNew ? 'Tạo sổ phép' : 'Sửa sổ phép', `${NHAN_SU_ID} năm ${NAM}: chuẩn ${row.PHEP_CHUAN}, tồn ${row.PHEP_TON_NAM_TRUOC}, điều chỉnh ${row.PHEP_DIEU_CHINH}`);
    return row;
}

/**
 * Tạo sổ phép năm cho người đang làm chưa có sổ. carryOver: chuyển số còn lại của năm trước sang làm phép tồn
 * (chỉ phần dương); người chưa có sổ năm trước thì tồn 0.
 */
export async function initializeLeaveYear(data, year, standard, { carryOver }) {
    assertCanManagePayroll();
    const has = new Set(data.balances.filter((balance) => balance.NAM === year).map((balance) => balance.NHAN_SU_ID));
    const previous = new Map(data.balances.filter((balance) => balance.NAM === year - 1).map((balance) => [balance.NHAN_SU_ID, balance]));
    const now = new Date();
    const rows = data.employees
        .filter((employee) => employee.SO_HD && employee.TRANG_THAI_LAM_VIEC !== 'Đã nghỉ' && !has.has(employee.SO_HD))
        .map((employee) => ({
            ID: balanceId(employee.SO_HD, year), NHAN_SU_ID: employee.SO_HD, NAM: year, PHEP_CHUAN: standard,
            PHEP_TON_NAM_TRUOC: carryOver ? Math.max(0, previous.get(employee.SO_HD)?.remaining ?? 0) : 0, PHEP_DIEU_CHINH: 0, GHI_CHU: '', UPDATED_AT: now,
        }));
    if (rows.length) await payrollRepos.leaveBalances.add(rows);
    await writeAudit('Khởi tạo sổ phép năm', { nam: year, phepChuan: standard, chuyenTon: Boolean(carryOver), them: rows.length });
    return rows.length;
}

/** Nhập sổ phép từ Excel: dòng đã có sổ thì sửa, chưa có thì thêm. */
export async function importLeaveBalances(data, rows) {
    assertCanManagePayroll();
    const existing = new Set(data.balances.map((balance) => balance.ID));
    const now = new Date();
    const prepared = rows.map((row) => ({ ID: balanceId(row.NHAN_SU_ID, row.NAM), ...row, GHI_CHU: row.GHI_CHU || '', UPDATED_AT: now }));
    const adds = prepared.filter((row) => !existing.has(row.ID));
    const edits = prepared.filter((row) => existing.has(row.ID));
    if (adds.length) await payrollRepos.leaveBalances.add(adds);
    if (edits.length) await payrollRepos.leaveBalances.update(edits);
    await writeAudit('Nhập sổ phép từ Excel', { them: adds.length, sua: edits.length });
    return { created: adds.length, updated: edits.length };
}

/** Số ngày của đơn theo lịch ngày lễ áp cho người đó (bỏ Chủ nhật, ngày lễ hưởng lương). */
export function previewLeaveDays(data, { NHAN_SU_ID, TU_NGAY, DEN_NGAY, NUA_NGAY }) {
    const keys = holidayDateKeys(TU_NGAY, DEN_NGAY, data.holidays, NHAN_SU_ID, data.teamsByEmployee.get(NHAN_SU_ID) ?? []);
    return buildLeaveDays(TU_NGAY, DEN_NGAY, keys, NUA_NGAY);
}

/**
 * Kiểm tra đơn trước khi lưu hoặc duyệt: dữ liệu, chồng ngày với đơn khác, có ngày cần nghỉ, sổ phép đủ (phép năm).
 * Trả về { errors, warnings, days, total }. Vượt số dư chỉ là cảnh báo với người quản lý (V2 cũng cho người
 * có quyền quản lý vượt), nhưng chưa có sổ phép năm thì chặn.
 */
export function checkLeaveRequest(data, request) {
    const errors = validateLeaveRequest(request);
    if (errors.length) return { errors, warnings: [], days: [], total: 0 };
    const { days, total } = previewLeaveDays(data, request);
    const warnings = [];
    if (total <= 0) errors.push('Khoảng nghỉ không có ngày làm việc nào cần tính.');
    const overlap = findLeaveOverlap(data.requests, request);
    if (overlap) errors.push(`Trùng ngày với đơn ${overlap.MA_DON} (${formatDay(overlap.TU_NGAY)} – ${formatDay(overlap.DEN_NGAY)}).`);
    if (LEAVE_TYPES[request.LOAI_PHEP]?.deduct) {
        const balance = data.balances.find((item) => item.NHAN_SU_ID === request.NHAN_SU_ID && item.NAM === request.TU_NGAY.getFullYear());
        if (!balance) errors.push(`Nhân sự chưa có sổ phép năm ${request.TU_NGAY.getFullYear()}.`);
        else {
            // Đơn đang sửa đã nằm trong số chờ duyệt / đã dùng của sổ, trừ ra để không tính hai lần.
            const own = data.requests.find((item) => item.MA_DON === request.MA_DON);
            const ownHeld = own && ['CHO_DUYET', 'DA_DUYET'].includes(own.TRANG_THAI) ? own.SO_NGAY : 0;
            const available = balance.remaining - balance.pending + ownHeld;
            if (total > available + 1e-9) warnings.push(`Vượt số phép còn lại: còn ${available} ngày (đã trừ đơn chờ duyệt), đơn này ${total} ngày.`);
        }
    }
    return { errors, warnings, days, total };
}

export async function saveLeaveRequest(data, request, { isNew, submit }) {
    assertCanManagePayroll();
    const check = checkLeaveRequest(data, request);
    if (check.errors.length) throw new Error(check.errors.join(' '));
    const user = currentPayrollUser().username;
    const row = {
        MA_DON: request.MA_DON || newId('DP'),
        NHAN_SU_ID: request.NHAN_SU_ID,
        NAM: request.TU_NGAY.getFullYear(),
        LOAI_PHEP: request.LOAI_PHEP,
        TU_NGAY: request.TU_NGAY,
        DEN_NGAY: request.DEN_NGAY,
        SO_NGAY: check.total,
        CHI_TIET_NGAY: check.days,
        LY_DO: request.LY_DO || '',
        TRANG_THAI: submit ? 'CHO_DUYET' : 'NHAP',
        LY_DO_TU_CHOI: '',
        ...(isNew ? { NGUOI_TAO: user, NGAY_TAO: new Date() } : {}),
    };
    if (isNew) await payrollRepos.leaveRequests.add([row]);
    else await payrollRepos.leaveRequests.update([row]);
    await writeAudit(isNew ? 'Tạo đơn nghỉ' : 'Sửa đơn nghỉ', `${row.MA_DON} ${row.NHAN_SU_ID} ${formatDay(row.TU_NGAY)} – ${formatDay(row.DEN_NGAY)}: ${row.SO_NGAY} ngày`);
    return row;
}

function assertStatus(request, allowed, message) {
    if (!allowed.includes(request.TRANG_THAI)) throw new Error(message);
}

export async function submitLeaveRequest(request) {
    assertCanManagePayroll();
    assertStatus(request, ['NHAP', 'TU_CHOI'], 'Đơn không ở trạng thái gửi duyệt được.');
    await payrollRepos.leaveRequests.update([{ MA_DON: request.MA_DON, TRANG_THAI: 'CHO_DUYET', LY_DO_TU_CHOI: '', NGUOI_DUYET: '', NGAY_DUYET: null }]);
    await writeAudit('Gửi duyệt đơn nghỉ', request.MA_DON);
}

/**
 * Duyệt đơn. Khác V2: người lập được tự duyệt, vì ở đây kế toán/quản lý nhập đơn hộ công nhân chứ công nhân
 * không tự tạo đơn. Ngày được tính lại theo lịch ngày lễ lúc duyệt.
 */
export async function approveLeaveRequest(data, request) {
    assertCanManagePayroll();
    assertStatus(request, ['CHO_DUYET'], 'Đơn không ở trạng thái chờ duyệt.');
    const user = currentPayrollUser().username;
    const check = checkLeaveRequest(data, request);
    if (check.errors.length) throw new Error(check.errors.join(' '));
    await payrollRepos.leaveRequests.update([{ MA_DON: request.MA_DON, TRANG_THAI: 'DA_DUYET', NGUOI_DUYET: user, NGAY_DUYET: new Date(), SO_NGAY: check.total, CHI_TIET_NGAY: check.days, LY_DO_TU_CHOI: '' }]);
    await writeAudit('Duyệt đơn nghỉ', `${request.MA_DON} ${request.NHAN_SU_ID}: ${check.total} ngày`);
}

export async function rejectLeaveRequest(request, reason) {
    assertCanManagePayroll();
    assertStatus(request, ['CHO_DUYET'], 'Đơn không ở trạng thái chờ duyệt.');
    if (!String(reason).trim()) throw new Error('Nhập lý do từ chối.');
    await payrollRepos.leaveRequests.update([{ MA_DON: request.MA_DON, TRANG_THAI: 'TU_CHOI', LY_DO_TU_CHOI: reason.trim(), NGUOI_DUYET: currentPayrollUser().username, NGAY_DUYET: new Date() }]);
    await writeAudit('Từ chối đơn nghỉ', `${request.MA_DON}: ${reason.trim()}`);
}

/** Hủy đơn đã duyệt: không được khi đơn nằm trong kỳ lương đã duyệt (số phép đã trả lương rồi). */
export async function cancelLeaveRequest(data, request, reason) {
    assertCanManagePayroll();
    assertStatus(request, ['DA_DUYET'], 'Chỉ hủy được đơn đã duyệt.');
    if (!String(reason).trim()) throw new Error('Nhập lý do hủy.');
    const period = data.approvedPeriods.find((item) => item.TU_NGAY <= request.DEN_NGAY && item.DEN_NGAY >= request.TU_NGAY);
    if (period) throw new Error(`Đơn nằm trong kỳ lương ${period.MA_KY} đã duyệt nên không hủy được. Mở lại kỳ lương trước.`);
    await payrollRepos.leaveRequests.update([{ MA_DON: request.MA_DON, TRANG_THAI: 'HUY', LY_DO_TU_CHOI: reason.trim(), NGUOI_DUYET: currentPayrollUser().username, NGAY_DUYET: new Date() }]);
    await writeAudit('Hủy đơn nghỉ', `${request.MA_DON}: ${reason.trim()}`);
}

export async function deleteLeaveRequest(request) {
    assertCanManagePayroll();
    assertStatus(request, ['NHAP', 'TU_CHOI'], 'Chỉ xóa được đơn nháp hoặc bị từ chối.');
    await payrollRepos.leaveRequests.remove([request.MA_DON]);
    await writeAudit('Xóa đơn nghỉ', request.MA_DON);
}
